import { MIN_ADULT_AGE } from 'constants/offer'
import { mapAgeCategories } from 'lib/offer/occupancyUtils'
import { NIGHTLY_DRINK_INCLUSION_REGEX } from './offerPageHighlightTagConstants'
import { type OfferPageHighlightTagContext } from './useOfferPageHighlightTags'

const PROPERTY_INCLUSION_SYMBOL_CATEGORY_MAP: Partial<Record<App.OfferFeatureSymbol, App.PackageInclusionCategory>> = {
  childs_face: 'One Child Stays Free',
  childs_face_with_plus: 'Multiple Children Stay Free',
  clock_with_check: 'Guaranteed Late Checkout',
  clock_with_plus: 'Late Checkout / Early Check-In',
  crossed_utensils_with_plus: 'Dining Credit',
  dollar_sign_in_circle: 'Daily Dining Credit',
  lotus: 'Spa Treatment',
  martini_glass: 'Nightly Cocktail',
  p_in_circle: 'Parking',
}

function isTruthyString(value: string | undefined): value is string {
  return !!value?.trim()
}

function getPropertyRoomSizeSqm(size: string | undefined) {
  const sqmMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:m²|m2|sqm|sq m)/i)
  if (sqmMatch?.[1]) return Number(sqmMatch[1])

  const sqftMatch = size?.match(/(\d+(?:\.\d+)?)\s*(?:ft²|ft2|sqft|sq ft)/i)
  if (sqftMatch?.[1]) return Number(sqftMatch[1]) * 0.092903

  return undefined
}

function getBedbankRoomFeatureText(pkg: App.BedbankPackage, featureId: string) {
  return pkg.features.find(feature => feature.id === featureId)?.text
}

function getBedbankRoomSizeSqm(pkg: App.BedbankPackage) {
  return getPropertyRoomSizeSqm(getBedbankRoomFeatureText(pkg, 'dimension'))
}

function getBedbankRoomViewText(pkg: App.BedbankPackage) {
  return getBedbankRoomFeatureText(pkg, 'water')
}

function hasChildrenCapacity(capacity: App.RoomCapacity) {
  return capacity.children > 0 || capacity.infants > 0 || capacity.teenagers > 0
}

function hasAdultsOnlyCapacities(capacities: Array<App.RoomCapacity> | undefined) {
  return !!capacities?.length && capacities.every(capacity => !hasChildrenCapacity(capacity))
}

/**
 * Capacity "adults-only" often uses the property's Adult age category, which can be 13+.
 * Highlight "Adults only" should only show for a true 18+ adult definition.
 * Unknown/unset thresholds leave capacity-based detection unchanged.
 *
 * Matches occupancy picker adult-age derivation:
 * `minAdultAge = Math.max(maxTeenagerAge, maxChildAge) + 1`
 */
function isAdultDefinitionEighteenPlus(
  thresholds?: Partial<Pick<App.AgeCategoryThresholds, 'maxChildAge' | 'maxTeenagerAge'>>,
) {
  const knownNonAdultAges = [thresholds?.maxTeenagerAge, thresholds?.maxChildAge].filter(
    (age): age is number => age != null && age >= 0,
  )
  if (knownNonAdultAges.length === 0) return true

  const minAdultAge = Math.max(...knownNonAdultAges) + 1
  return minAdultAge >= MIN_ADULT_AGE
}

function isAdultDefinitionEighteenPlusFromAgeCategories(
  ageCategories: Array<App.PackageRoomAgeCategory> | undefined,
) {
  if (!ageCategories?.length) return true

  return isAdultDefinitionEighteenPlus(mapAgeCategories(ageCategories))
}

function getPropertyRoomTexts(room: App.Property.Room) {
  return [
    room.description,
    ...room.popularAmenities.map(amenity => amenity.name),
    ...room.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
  ].filter(isTruthyString)
}

function getPropertyFacilityNames(property: App.Property.Property) {
  return [
    ...property.popularFacilities.map(facility => facility.name),
    ...property.amenities.flatMap(amenityGroup => amenityGroup.items.map(amenity => amenity.name)),
  ].filter(isTruthyString)
}

function getPackageInclusionDescriptions(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
    ...Object.values(pkg.luxPlusInclusionsByTier ?? {}).flat(),
  ].map(inclusion => inclusion.description)
}

function getRateInclusions(rate: App.EnrichedPropertyRate) {
  return [
    ...(rate.inclusions ?? []),
    ...(rate.bonusInclusions ?? []),
    ...(rate.luxPlusInclusions ?? []),
    ...(rate.bundleInclusions ?? []),
  ]
}

function hasBreakfastInclusion(inclusions: Array<App.OfferInclusion>) {
  return inclusions.some(inclusion => /\bbreakfast\b/i.test(inclusion.description))
}

function hasBreakfastText(textItems: Array<string>) {
  return textItems.some(text => /\bbreakfast\b/i.test(text))
}

function hasAllInclusiveText(textItems: Array<string>) {
  const text = textItems.join(' ')

  return /\ball[-\s]?inclusive\b/i.test(text) || (
    /\b(all meals|full board)\b/i.test(text) &&
    /\b(unlimited drinks|drinks included)\b/i.test(text)
  )
}

function hasMassageText(textItems: Array<string>) {
  return textItems.some(text => /\bmassages?\b/i.test(text))
}

function hasNightlyDrinkText(textItems: Array<string>) {
  return textItems.some(text => NIGHTLY_DRINK_INCLUSION_REGEX.test(text))
}

function hasBreakfastIncludedOnAllPropertyRates(
  roomsWithRates: Array<App.Property.Room>,
  propertyRates: Array<App.EnrichedPropertyRate>,
) {
  if (propertyRates.length > 0) {
    return propertyRates.every(rate => hasBreakfastInclusion(getRateInclusions(rate)))
  }

  return roomsWithRates.length > 0 && roomsWithRates.every(room => hasBreakfastInclusion(getPropertyRoomInclusions(room)))
}

function hasBreakfastIncludedOnAllBedbankRates(rates: Array<App.BedbankRate>) {
  return rates.length > 0 && rates.every(rate => hasBreakfastInclusion(rate.inclusions))
}

function getCommonInclusionCategories(categoryGroups: Array<Array<App.PackageInclusionCategory>>) {
  if (categoryGroups.length === 0) return []

  const [firstCategoryGroup, ...remainingCategoryGroups] = categoryGroups
  return Array.from(new Set(firstCategoryGroup)).filter(category =>
    remainingCategoryGroups.every(categoryGroup => categoryGroup.includes(category)),
  )
}

function hasAllInclusiveCategory(categories: Array<App.PackageInclusionCategory>) {
  return categories.includes('All-Inclusive Dining')
}

function getPackageInclusionCategories(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
    ...Object.values(pkg.luxPlusInclusionsByTier ?? {}).flat(),
  ].map(inclusion => inclusion.category).filter((category): category is App.PackageInclusionCategory => !!category)
}

function getOfferInclusionCategory(inclusion: App.OfferInclusion) {
  const category = (inclusion as { category?: App.PackageInclusionCategory }).category
  return category ?? (inclusion.symbol ? PROPERTY_INCLUSION_SYMBOL_CATEGORY_MAP[inclusion.symbol] : undefined)
}

function getRateInclusionCategories(rate: App.EnrichedPropertyRate) {
  return getRateInclusions(rate)
    .map(getOfferInclusionCategory)
    .filter((category): category is App.PackageInclusionCategory => !!category)
}

function getPropertyRoomInclusions(room: App.Property.Room) {
  return [
    ...room.inclusions,
    ...room.bonusInclusions,
    ...room.luxPlusInclusions,
  ]
}

function getPropertyRoomInclusionCategories(room: App.Property.Room) {
  return getPropertyRoomInclusions(room)
    .map(getOfferInclusionCategory)
    .filter((category): category is App.PackageInclusionCategory => !!category)
}

function hasAllInclusivePackageSignal(pkg: App.HotelPackage) {
  return hasAllInclusiveCategory(getPackageInclusionCategories(pkg)) ||
    hasAllInclusiveText(getPackageInclusionDescriptions(pkg))
}

function hasAllInclusivePropertyRateSignal(rate: App.EnrichedPropertyRate) {
  const rateInclusions = getRateInclusions(rate)

  return rate.boardCode === 'allinclusive' ||
    hasAllInclusiveCategory(getRateInclusionCategories(rate)) ||
    hasAllInclusiveText(rateInclusions.map(inclusion => inclusion.description))
}

function hasAllInclusivePropertyRoomSignal(room: App.Property.Room) {
  const roomInclusions = getPropertyRoomInclusions(room)

  return hasAllInclusiveCategory(getPropertyRoomInclusionCategories(room)) ||
    hasAllInclusiveText(roomInclusions.map(inclusion => inclusion.description))
}

function hasAllInclusiveBedbankRateSignal(rate: App.BedbankRate) {
  return rate.boardCode === 'allinclusive' ||
    hasAllInclusiveText(rate.inclusions.map(inclusion => inclusion.description))
}

function hasAdultsOnlyHotelPackageCapacity(pkg: App.HotelPackage) {
  return hasAdultsOnlyCapacities(pkg.roomRate?.capacities)
}

function hasAdultsOnlyPropertyRoomCapacity(room: App.Property.Room) {
  return hasAdultsOnlyCapacities(room.capacities) &&
    isAdultDefinitionEighteenPlus(room.ageCategoryThresholds)
}

function hasAdultsOnlyBedbankPackageCapacity(pkg: App.BedbankPackage) {
  return hasAdultsOnlyCapacities(pkg.capacities?.combinations) &&
    isAdultDefinitionEighteenPlusFromAgeCategories(pkg.capacities?.ageCategories)
}

function getHotelPackageRoomTexts(pkg: App.HotelPackage) {
  return [
    pkg.roomType.description,
    ...pkg.roomType.amenities.map(amenity => amenity.description),
  ].filter(isTruthyString)
}

export function buildFlashHotelHighlightTagContext(
  offer: App.HotelOffer,
  property: App.OfferProperty,
  packageViews?: Array<App.OfferPackageOptionView>,
  charterBundleRoute?: App.CharterBundleRoute,
): OfferPageHighlightTagContext {
  const hasPackageViewContext = !!packageViews
  const packages = packageViews?.filter(view => view.hotelAvailable && view.capacityValid).map(view => view.package) ?? offer.packages
  const packagesForAdultCapacity = packageViews?.filter(view => view.hotelAvailable).map(view => view.package) ?? offer.packages
  const packageInclusionDescriptions = packages.flatMap(getPackageInclusionDescriptions)
  const packageBoardCodes = packages.map(pkg => pkg.boardCode)
  const inclusionCategories = getCommonInclusionCategories(packages.map(getPackageInclusionCategories))

  return {
    boardCodes: packageBoardCodes,
    charterBundleRoute,
    facilities: [
      offer.facilities,
      offer.highlights,
      offer.gettingThere,
      property.address,
      property.geoData.locality,
      property.geoData.administrativeAreaLevel1,
    ].filter(isTruthyString),
    hasAllInclusiveOnAllRates: packages.length > 0 && packages.every(hasAllInclusivePackageSignal),
    hasBreakfastIncludedOnAllRates: packages.length > 0 && packages.every(pkg => hasBreakfastText(getPackageInclusionDescriptions(pkg))),
    hasMassageIncludedOnAllRates: packages.length > 0 && packages.every(pkg => hasMassageText(getPackageInclusionDescriptions(pkg))),
    hasNightlyDrinkOnAllRates: packages.length > 0 && packages.every(pkg => hasNightlyDrinkText(getPackageInclusionDescriptions(pkg))),
    holidayTypes: offer.holidayTypes,
    inclusions: [
      ...(!hasPackageViewContext && offer.inclusions ? [offer.inclusions] : []),
      ...packageInclusionDescriptions,
    ].filter(isTruthyString),
    inclusionCategories,
    isAdultsOnly: packagesForAdultCapacity.length > 0 &&
      packagesForAdultCapacity.every(hasAdultsOnlyHotelPackageCapacity) &&
      isAdultDefinitionEighteenPlus({
        maxChildAge: property.maxChildAge,
        maxTeenagerAge: property.maxTeenagerAge,
      }),
    rating: property.rating,
    roomAmenities: packages.flatMap(getHotelPackageRoomTexts),
    roomNames: packages.map(pkg => pkg.roomType.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(pkg => pkg.roomType.sizeSqm).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildPropertyHighlightTagContext(
  property: App.Property.Property,
  propertyRates: Array<App.EnrichedPropertyRate>,
): OfferPageHighlightTagContext {
  const rooms = Object.values(property.rooms)
  const availableRoomIds = new Set(propertyRates.map(rate => rate.roomId))
  const roomsWithRates = propertyRates.length > 0 ? rooms.filter(room => availableRoomIds.has(room.id)) : rooms
  const roomInclusions = roomsWithRates.flatMap(getPropertyRoomInclusions)
  const hasBreakfastIncludedOnAllRates = hasBreakfastIncludedOnAllPropertyRates(roomsWithRates, propertyRates)
  const facilityNames = getPropertyFacilityNames(property)
  const textSignalSource = propertyRates.length > 0 ?
      propertyRates.map(rate => getRateInclusions(rate).map(inclusion => inclusion.description)) :
      roomsWithRates.map(room => getPropertyRoomInclusions(room).map(inclusion => inclusion.description))
  const inclusionCategories = getCommonInclusionCategories(
    propertyRates.length > 0 ?
        propertyRates.map(getRateInclusionCategories) :
        roomsWithRates.map(getPropertyRoomInclusionCategories),
  )
  const propertyBoardCodes = propertyRates.map(rate => rate.boardCode)

  return {
    boardCodes: propertyBoardCodes,
    facilities: [
      property.highlights,
      property.locationDescription,
      property.facilities,
      property.location.title,
      property.location.formattedAddress,
      property.location.city,
      property.location.region,
      ...facilityNames,
    ].filter(isTruthyString),
    facilityNames,
    hasAllInclusiveOnAllRates: propertyRates.length > 0 ?
        propertyRates.every(hasAllInclusivePropertyRateSignal) :
      roomsWithRates.length > 0 && roomsWithRates.every(hasAllInclusivePropertyRoomSignal),
    hasBreakfastIncludedOnAllRates,
    hasMassageIncludedOnAllRates: textSignalSource.length > 0 && textSignalSource.every(hasMassageText),
    hasNightlyDrinkOnAllRates: textSignalSource.length > 0 && textSignalSource.every(hasNightlyDrinkText),
    holidayTypes: property.holidayTypeTags,
    inclusions: roomInclusions.map(inclusion => inclusion.description),
    inclusionCategories,
    isAdultsOnly: roomsWithRates.length > 0 && roomsWithRates.every(hasAdultsOnlyPropertyRoomCapacity),
    locationTags: property.locationTags,
    rating: property.rating,
    roomAmenities: roomsWithRates.flatMap(getPropertyRoomTexts),
    roomNames: roomsWithRates.map(room => room.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: roomsWithRates.length,
    roomSizesSqm: roomsWithRates.map(room => getPropertyRoomSizeSqm(room.size)).filter((size): size is number => typeof size === 'number'),
  }
}

export function buildBedbankHighlightTagContext(
  offer: App.BedbankOffer,
  hotelOnlyRates?: Array<App.BedbankRate>,
): OfferPageHighlightTagContext {
  const hasRateContext = !!hotelOnlyRates
  const packages = hotelOnlyRates?.map(rate => rate.room) ?? offer.packages
  const packagesForAdultCapacity = hasRateContext && packages.length > 0 ? packages : offer.packages
  const boardCodes = hotelOnlyRates?.map(rate => rate.boardCode)
  const facilityNames = [
    ...offer.popularFacilities,
    ...offer.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
  ].filter(isTruthyString)

  return {
    boardCodes,
    facilities: [
      offer.amenities,
      offer.attractions,
      offer.description,
      offer.tagline,
      ...facilityNames,
    ].filter(isTruthyString),
    facilityNames,
    hasAllInclusiveOnAllRates: hasRateContext ? hotelOnlyRates.length > 0 && hotelOnlyRates.every(hasAllInclusiveBedbankRateSignal) : undefined,
    hasBreakfastIncludedOnAllRates: hasRateContext ?
        hasBreakfastIncludedOnAllBedbankRates(hotelOnlyRates) :
        hasBreakfastText((offer.inclusions ?? []).map(inclusion => inclusion.description)),
    hasMassageIncludedOnAllRates: hasRateContext ? hotelOnlyRates.length > 0 && hotelOnlyRates.every(rate => hasMassageText(rate.inclusions.map(inclusion => inclusion.description))) : undefined,
    hasNightlyDrinkOnAllRates: hasRateContext ? hotelOnlyRates.length > 0 && hotelOnlyRates.every(rate => hasNightlyDrinkText(rate.inclusions.map(inclusion => inclusion.description))) : undefined,
    holidayTypes: offer.holidayTypes ?? [],
    inclusions: hasRateContext ? [] : (offer.inclusions ?? []).map(inclusion => inclusion.description),
    isAdultsOnly: packagesForAdultCapacity.length > 0 && packagesForAdultCapacity.every(hasAdultsOnlyBedbankPackageCapacity),
    rating: offer.property.rating,
    roomAmenities: packages.flatMap(pkg => [
      pkg.description,
      ...pkg.features.map(feature => feature.text),
      ...pkg.facilityGroups.flatMap(facilityGroup => facilityGroup.values.map(facility => facility.name)),
    ]).filter(isTruthyString),
    roomNames: packages.map(pkg => pkg.name).filter(isTruthyString),
    roomSizeAvailableRoomCount: packages.length,
    roomSizesSqm: packages.map(getBedbankRoomSizeSqm).filter((size): size is number => typeof size === 'number'),
    roomViewTags: packages.map(getBedbankRoomViewText).filter(isTruthyString),
  }
}
